import React, { useState, useEffect } from 'react'
import { useFetch, usePut } from '../../../components/component-items/helpers'
import { globalState } from '../../../store'

// Components
import CardLoading from '../../../components/component-items/loading-popover'
import GlobalStyles from '../../../components/component-items/styles'
import { Form } from 'react-bootstrap'
import { TabMenu } from '../../../components/component-items/tab-menu'
import { WarehouseCompanySettingsProps, CompanySettingsProps } from './types'
import { WarehouseManagementSystem } from '../../../enums'

// Form Components
import {
  CompanyBasicInfoFields,
  OrderManagementFields,
  WarehouseManagementFields,
  TransportationManagementFields,
} from './formsets'

type SettingsState = CompanySettingsProps & Partial<WarehouseCompanySettingsProps>

const defaultSettings: SettingsState = {
  // Company Settings
  name: '',
  supportEmail: '',
  fulfillmentName: '',
  grading: '30',
  wireTransferMerchant: false,
  gestationMinutes: 120,
  minimumRemainingShelfLife: 90,
  maxShippingAmount: null,
  emailCustomer: true,
  useIntegrationPrices: false,
  dontShipOutsideUsa: false,
  pauseReplacementOrders: false,
  // Warehouse Settings
  warehouse: '',
  wms: WarehouseManagementSystem.Local,
  maId: '',
  activeWarehouses: [],
  orderRoutingWarehouses: [],
  useOnlyInStockWarehouses: false,
  maxDeliveryDays: null,
  active: true,
  enableFulfillment: false,
  testing: false,
  fastPick: false,
  autoWaveOrders: false,
  usePassport: false,
}

interface Props {
  context: 'company' | 'warehouse'
}

export const CompanySettings = ({ context }: Props) => {
  const [settings, setSettings] = useState<SettingsState>(defaultSettings)
  const [validated, setValidated] = useState(false)
  const [activeTab, setActiveTab] = useState('Business Settings')

  const {
    state: { csrf, companyId },
  } = globalState()

  const { response: resp, error, loaded, placeholder } = useFetch(`/api/company/${companyId}/`, {})
  const warehouseList = loaded && resp ? resp.warehouses : []
  const warehouses = warehouseList.map((w: any) => ({ value: w.id, label: w.name }))

  useEffect(() => {
    if (!loaded || !resp) return

    const newSettings = {
      // Company Settings
      name: resp.name,
      supportEmail: resp.supportEmail,
      fulfillmentName: resp.fulfillmentName,
      grading: resp.grading,
      wireTransferMerchant: resp.wireTransferMerchant,
      gestationMinutes: resp.gestationMinutes,
      minimumRemainingShelfLife: resp.minimumRemainingShelfLife,
      maxShippingAmount: resp.maxShippingAmount,
      emailCustomer: resp.emailCustomer,
      useIntegrationPrices: resp.useIntegrationPrices,
      dontShipOutsideUsa: resp.dontShipOutsideUsa,
      pauseReplacementOrders: resp.pauseReplacementOrders,
      useOnlyInStockWarehouses: resp.useOnlyInStockWarehouses ?? false,
    }

    if (context === 'warehouse') {
      Object.assign(newSettings, {
        warehouse: resp.warehouse?.id ?? '',
        activeWarehouses: resp.activeWarehouses.map((w: any) => ({ value: w.id, label: w.name })),
        orderRoutingWarehouses: (resp.orderRoutingWarehouses ?? []).map((w: any) => ({
          value: w.id,
          label: w.name,
        })),
        maxDeliveryDays: resp.maxDeliveryDays ?? null,
        active: resp.active ?? true,
        enableFulfillment: resp.enableFulfillment ?? false,
        testing: resp.testing ?? false,
        fastPick: resp.fastPick ?? false,
        autoWaveOrders: resp.autoWaveOrders ?? false,
        usePassport: resp.usePassport ?? false,
        wms: resp.wms ?? WarehouseManagementSystem.Local,
        maId: resp.maId ?? '',
      })
    }

    setSettings(newSettings)
  }, [loaded, context])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setValidated(true)

    const form = event.target as HTMLFormElement
    if (!form.checkValidity()) return

    const dataToSend =
      context === 'warehouse'
        ? settings
        : {
            name: settings.name,
            supportEmail: settings.supportEmail,
            fulfillmentName: settings.fulfillmentName,
            grading: settings.grading,
            wireTransferMerchant: settings.wireTransferMerchant,
            gestationMinutes: settings.gestationMinutes,
            minimumRemainingShelfLife: settings.minimumRemainingShelfLife,
            maxShippingAmount: settings.maxShippingAmount,
            emailCustomer: settings.emailCustomer,
            useIntegrationPrices: settings.useIntegrationPrices,
            dontShipOutsideUsa: settings.dontShipOutsideUsa,
            pauseReplacementOrders: settings.pauseReplacementOrders,
            useOnlyInStockWarehouses: settings.useOnlyInStockWarehouses,
          }

    usePut(`/api/company/${companyId}/settings/`, dataToSend, csrf)
  }

  const handleSettingsChange = (newSettings: Partial<SettingsState>) => {
    setSettings((prev) => ({ ...prev, ...newSettings }))
  }

  const tabs = {
    'Business Settings': (
      <CompanyBasicInfoFields
        settings={settings}
        setSettings={handleSettingsChange}
        validated={validated}
        context={context}
      />
    ),
    'Order Management': (
      <OrderManagementFields settings={settings} setSettings={handleSettingsChange} context={context} />
    ),
    'Warehouse Configuration': (
      <WarehouseManagementFields
        settings={settings as WarehouseCompanySettingsProps}
        setSettings={handleSettingsChange}
        warehouseList={warehouseList}
        warehouses={warehouses}
        context={context}
      />
    ),
    'Shipping & Postage': (
      <TransportationManagementFields
        settings={settings as WarehouseCompanySettingsProps}
        setSettings={handleSettingsChange}
        context={context}
      />
    ),
  }

  return (
    <GlobalStyles.FullPageCard>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <GlobalStyles.CardHeader>
          <GlobalStyles.CardTitle>
            <h3>{settings.name || 'Company'} Settings</h3>
          </GlobalStyles.CardTitle>
          <GlobalStyles.CardToolbar>
            <GlobalStyles.Button type="submit">Update</GlobalStyles.Button>
          </GlobalStyles.CardToolbar>
        </GlobalStyles.CardHeader>
        <GlobalStyles.CardHeader style={{ justifyContent: 'center' }}>
          <TabMenu tabOptions={Object.keys(tabs)} activeTab={activeTab} setActiveTab={setActiveTab} />
        </GlobalStyles.CardHeader>
        <div style={{ padding: '2em', maxWidth: 800, margin: 'auto' }}>{tabs[activeTab as keyof typeof tabs]}</div>
      </Form>
      {!loaded && <CardLoading text={placeholder} error={error} />}
    </GlobalStyles.FullPageCard>
  )
}
